'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { apiService } from '@/lib/api-client';
import { SmsCodeType } from '@/lib/types/api';

interface PhoneVerificationPopupProps {
  isOpen: boolean;
  onClose: () => void;
  phoneNumber: string;
  onVerificationSuccess: (smsCode: string) => void;
  codeType?: SmsCodeType;
}

export default function PhoneVerificationPopup({
  isOpen,
  onClose,
  phoneNumber,
  onVerificationSuccess,
  codeType = SmsCodeType.Register
}: PhoneVerificationPopupProps) {
  const t = useTranslations('phoneVerification');
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [isCodeSent, setIsCodeSent] = useState(false);

  // Countdown timer
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Reset form when popup opens
  useEffect(() => {
    if (isOpen) {
      setVerificationCode(['', '', '', '', '', '']);
      setError('');
      setCountdown(0);
      setIsCodeSent(false);
      setIsLoading(false);
      // Otomatik olarak SMS kodu gönder
      sendSmsCode();
    }
  }, [isOpen]);

  // SMS kodu gönder
  const sendSmsCode = async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = await apiService.smsCode.send({
        phoneNumber: phoneNumber.replace(/\s/g, ''), // Boşlukları kaldır
        codeType: codeType
      });

      if (response.isSuccess) {
        setIsCodeSent(true);
        setCountdown(120); // 2 dakika (120 saniye) countdown
      } else {
        setError(response.message || 'SMS kodu gönderilemedi');
      }
    } catch (error) {
      // Error handling
      setError('SMS kodu gönderilirken bir hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeChange = (index: number, value: string) => {
    if (value.length > 1) return; // Sadece tek karakter
    
    const newCode = [...verificationCode];
    newCode[index] = value;
    setVerificationCode(newCode);

    // Otomatik sonraki input'a geç
    if (value && index < 5) {
      const nextInput = document.getElementById(`code-${index + 1}`) as HTMLInputElement;
      if (nextInput) nextInput.focus();
    }

    // Otomatik önceki input'a geç (backspace)
    if (!value && index > 0) {
      const prevInput = document.getElementById(`code-${index - 1}`) as HTMLInputElement;
      if (prevInput) prevInput.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      const prevInput = document.getElementById(`code-${index - 1}`) as HTMLInputElement;
      if (prevInput) prevInput.focus();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const code = verificationCode.join('');
    if (code.length !== 6) {
      setError('Kod 6 haneli olmalıdır');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await apiService.smsCode.verify({
        phoneNumber: phoneNumber.replace(/\s/g, ''), // Boşlukları kaldır
        code: code,
        codeType: codeType
      });

      // Backend'den gelen response format'ını kontrol et
      if (response.isSuccess) {
        // Backend response format: { data: boolean, isSuccess: true }
        if (response.data === true) {
          onVerificationSuccess(code);
          onClose();
        } else {
          setError('Geçersiz kod');
        }
      } else {
        setError(response.message || 'Geçersiz kod');
      }
    } catch (error) {
      // Error handling
      setError('Kod doğrulanırken bir hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    await sendSmsCode();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      ></div>
      
      {/* Popup */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-8">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('title')}</h2>
          <p className="text-gray-600">{t('subtitle')}</p>
        </div>

        {/* Phone number display */}
        <div className="text-center mb-6">
          <p className="text-sm text-gray-600 mb-1">{t('sentTo')}</p>
          <p className="text-lg font-semibold text-gray-900">{phoneNumber}</p>
        </div>

        {/* Verification form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Code inputs */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              {t('enterCode')}
            </label>
            <div className="flex justify-center space-x-2">
              {verificationCode.map((digit, index) => (
                <input
                  key={index}
                  id={`code-${index}`}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleCodeChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  className="w-10 h-12 text-center text-xl font-bold border-2 border-gray-300 rounded-lg focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/20 transition-colors"
                  placeholder="•"
                />
              ))}
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div className="text-center">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {/* Submit button */}
          <button
            type="submit"
            disabled={isLoading || verificationCode.join('').length !== 6}
            className="w-full py-3 px-4 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {isLoading ? t('verifying') : t('verify')}
          </button>
        </form>

        {/* Resend code */}
        <div className="text-center mt-6">
          <p className="text-sm text-gray-600 mb-2">{t('didntReceive')}</p>
          <button
            onClick={handleResendCode}
            disabled={isLoading || countdown > 0}
            className="text-red-600 hover:text-red-700 font-medium disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            {countdown > 0 
              ? `${t('resendIn')} ${countdown}s` 
              : t('resendCode')
            }
          </button>
        </div>

        {/* Loading state */}
        {isLoading && !isCodeSent && (
          <div className="mt-6 p-3 bg-blue-50 rounded-lg">
            <p className="text-xs text-blue-700 text-center">
              SMS kodu gönderiliyor...
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
